/**
 * FAQ page: topic scroll-spy menu, live search and the Q/A accordions.
 *
 * Was `sections/faq.liquid`'s {% javascript %} tag, so it rode `scripts.js` onto every
 * page of the store for the sake of one template. Loaded from the section now.
 *
 * The IIFE is not decoration: Shopify wraps every {% javascript %} tag in one, and
 * without it `class FaqPage` would be a global lexical binding — a second <script src>
 * on the same page (two FAQ sections) would throw "Identifier already declared" before
 * the guarded `customElements.define` at the bottom ever ran.
 */
(function () {
  class FaqPage extends HTMLElement {
    connectedCallback() {
      this.nav = this.querySelector('.faq__nav');
      this.tabs = Array.from(this.querySelectorAll('.faq__tab'));
      this.topics = Array.from(this.querySelectorAll('[data-topic]'));
      this.items = Array.from(this.querySelectorAll('[data-faq-item]'));
      this.searchInput = this.querySelector('.faq__search-input');
      this.emptyState = this.querySelector('[data-empty]');
      this.toTop = this.querySelector('[data-to-top]');
      this.sentinel = this.querySelector('[data-top-sentinel]');

      this.updateNavHeight();
      this.onResize = this.updateNavHeight.bind(this);
      window.addEventListener('resize', this.onResize);

      this.tabs.forEach((tab) => tab.addEventListener('click', this.onTabClick.bind(this)));
      this.addEventListener('click', this.onAccordionClick.bind(this));
      this.setupScrollSpy();
      this.setupBackToTop();

      if (this.searchInput) {
        this.searchInput.addEventListener('input', this.onSearch.bind(this));
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.topObserver) this.topObserver.disconnect();
      if (this.onResize) window.removeEventListener('resize', this.onResize);
    }

    updateNavHeight() {
      if (!this.nav) return;
      this.style.setProperty('--nav-height', this.nav.offsetHeight + 'px');
    }

    /* Accordion: clicking a question toggles its own answer. */
    onAccordionClick(event) {
      const toggle = event.target.closest('[data-faq-toggle]');
      if (!toggle || !this.contains(toggle)) return;
      const item = toggle.closest('[data-faq-item]');
      if (!item) return;
      const open = item.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    setupScrollSpy() {
      if (!this.tabs.length || !this.topics.length) return;
      this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
        rootMargin: '-10px 0px -60% 0px',
        threshold: 0,
      });
      this.topics.forEach((topic) => this.observer.observe(topic));
    }

    onIntersect(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) this.setActive('#' + entry.target.id);
      });
    }

    setupBackToTop() {
      if (!this.toTop || !this.sentinel) return;
      this.topObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.toTop.classList.toggle('is-visible', !entry.isIntersecting);
        });
      }, { threshold: 0 });
      this.topObserver.observe(this.sentinel);
      this.toTop.addEventListener('click', () => {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    onTabClick(event) {
      const href = event.currentTarget.getAttribute('href') || '';
      if (href.charAt(0) !== '#') return;
      const id = href.slice(1);
      const selector = '#' + (window.CSS && CSS.escape ? CSS.escape(id) : id);
      const target = this.querySelector(selector);
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.setActive(href);
    }

    setActive(href) {
      this.tabs.forEach((tab) => {
        tab.classList.toggle('is-active', tab.getAttribute('href') === href);
      });
    }

    tabForTopic(topic) {
      const href = '#' + topic.id;
      return this.tabs.find((tab) => tab.getAttribute('href') === href);
    }

    onSearch() {
      const rawQuery = this.searchInput.value.trim();
      const query = rawQuery.toLowerCase();
      const searching = query.length > 0;
      let anyVisible = false;

      this.topics.forEach((topic) => {
        let topicHasVisible = false;

        topic.querySelectorAll('[data-group]').forEach((group) => {
          let groupHasVisible = false;

          group.querySelectorAll('[data-faq-item]').forEach((item) => {
            const haystack = item.getAttribute('data-search') || '';
            const match = !searching || haystack.indexOf(query) !== -1;
            item.hidden = !match;
            // Reveal the answer of matches while searching; collapse when cleared.
            item.classList.toggle('is-open', searching && match);
            const toggle = item.querySelector('[data-faq-toggle]');
            if (toggle) toggle.setAttribute('aria-expanded', searching && match ? 'true' : 'false');
            // Underline the matched term inside the question and (revealed) answer.
            this.highlight(item.querySelector('.faq__question-text'), searching && match ? rawQuery : '');
            this.highlight(item.querySelector('.faq__answer'), searching && match ? rawQuery : '');
            if (match) groupHasVisible = true;
          });

          group.hidden = !groupHasVisible;
          if (groupHasVisible) topicHasVisible = true;
        });

        topic.hidden = !topicHasVisible;
        if (topicHasVisible) anyVisible = true;
        const tab = this.tabForTopic(topic);
        if (tab) tab.classList.toggle('is-empty', !topicHasVisible);
      });

      if (this.emptyState) this.emptyState.hidden = !searching || anyVisible;
    }

    /* Wrap every case-insensitive occurrence of `term` inside `root` in a
       <mark class="faq__hl">, walking text nodes only so tags (links, lists)
       stay intact. An empty term just clears any existing highlights. */
    highlight(root, term) {
      if (!root) return;
      // Remove previous marks first (unwrap + merge adjacent text nodes).
      root.querySelectorAll('mark.faq__hl').forEach((m) => {
        m.replaceWith(document.createTextNode(m.textContent));
      });
      root.normalize();
      if (!term) return;

      const needle = term.toLowerCase();
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
      const targets = [];
      let node;
      while ((node = walker.nextNode())) {
        if (node.nodeValue.toLowerCase().indexOf(needle) !== -1) targets.push(node);
      }

      targets.forEach((textNode) => {
        const text = textNode.nodeValue;
        const lower = text.toLowerCase();
        const frag = document.createDocumentFragment();
        let from = 0;
        let at;
        while ((at = lower.indexOf(needle, from)) !== -1) {
          if (at > from) frag.appendChild(document.createTextNode(text.slice(from, at)));
          const mark = document.createElement('mark');
          mark.className = 'faq__hl';
          mark.textContent = text.slice(at, at + needle.length);
          frag.appendChild(mark);
          from = at + needle.length;
        }
        if (from < text.length) frag.appendChild(document.createTextNode(text.slice(from)));
        textNode.replaceWith(frag);
      });
    }
  }

  if (!customElements.get('faq-page')) {
    customElements.define('faq-page', FaqPage);
  }
})();
